import { useCallback, useRef, useState } from "react"
import { Copy } from "lucide-react"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@prescreen/lib/utils"

const COPIED_TOOLTIP_MS = 3000

type CredentialCopyRowProps = {
  value: string
  className?: string
}

export function CredentialCopyRow({ value, className }: CredentialCopyRowProps) {
  const [copiedTooltipOpen, setCopiedTooltipOpen] = useState(false)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }, [])

  const handleCopy = useCallback(async () => {
    clearCloseTimer()
    try {
      await navigator.clipboard.writeText(value)
      setCopiedTooltipOpen(true)
      closeTimerRef.current = setTimeout(() => {
        setCopiedTooltipOpen(false)
        closeTimerRef.current = null
      }, COPIED_TOOLTIP_MS)
    } catch {
      setCopiedTooltipOpen(false)
    }
  }, [value, clearCloseTimer])

  const handleTooltipOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        clearCloseTimer()
        setCopiedTooltipOpen(false)
      }
    },
    [clearCloseTimer],
  )

  return (
    <div
      className={cn(
        "mt-1 flex items-center gap-2 rounded border bg-gray-100 p-3",
        className,
      )}
    >
      <div className="min-w-0 flex-1 self-center font-mono text-sm break-all">
        {value}
      </div>
      <Tooltip
        open={copiedTooltipOpen}
        onOpenChange={handleTooltipOpenChange}
        delayDuration={0}
      >
        <TooltipTrigger asChild>
          <button
            type="button"
            disabled={!value}
            onClick={handleCopy}
            className={cn(
              "inline-flex shrink-0 self-center rounded-md p-1.5 text-muted-foreground",
              "transition-colors hover:bg-gray-200/80 hover:text-foreground",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              "disabled:pointer-events-none disabled:opacity-40",
            )}
            aria-label="Copy to clipboard"
          >
            <Copy className="h-4 w-4" aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent side="left">Copied!</TooltipContent>
      </Tooltip>
    </div>
  )
}
